import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { sendRequest } from "../../utils/api";
import Service from "../../models/Service";



export default function ItemPage() {
    const navigate = useNavigate();
    //    const [searchParams] = useSearchParams();

    const ITEMS_PER_PAGE = 9;
    const [page, setPage] = useState(1);


    const [search, setSearch] = useState("");
    const [items, setItems] = useState<Service[]>([]);
    const [filteredItems, setFilteredItems] = useState<Service[]>([]);
    const [loading, setLoading] = useState(true);

    const availableItem = ["drinks", "foods", "games", "laundries", "rooms"];

    const { type } = useParams<{ type: string }>();

    useEffect(() => {
        const fetchItems = async () => {
            const isAvailable = availableItem.includes(type ?? "");

            if (!isAvailable) {
                navigate("/guests/dashboard");
                return;
            }

            let itemType = type?.slice(0, -1);

            if (type === "laundries") itemType = "laundry"

            try {
                const data = await sendRequest(
                    "GET",
                    `/services?type=${itemType}`
                );

                setItems(data);
                setFilteredItems(data);
            } catch (err) {
                console.error("Failed to load items:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, [type, navigate]);

    useEffect(() => {
        const lower = search.toLowerCase();

        const result = items.filter(
            (item) =>
                item.name.toLowerCase().includes(lower) ||
                item.category.toLowerCase().includes(lower)
        );

        setFilteredItems(result);
        setPage(1); // reset page when searching
    }, [search, items]);

    const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

    const paginatedItems = filteredItems.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-14 w-14 border-4 border-indigo-500 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-gray-50 p-4 flex flex-col">
            <div className="mx-auto w-full flex flex-col h-full">

                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <button onClick={() => navigate(-1)}>
                        {FaArrowLeft({ size: 20 })}
                    </button>

                    <h1 className="text-3xl font-semibold text-gray-800 capitalize">
                        {type} Menu
                    </h1>
                </div>

                <p className="text-gray-500 mb-4">
                    Browse our available {type} and check pricing.
                </p>

                {/* Search Bar */}
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder={`Search ${type?.toLowerCase()}...`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full md:w-1/2 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                {/* Scrollable Items Section */}
                <div className="flex-1 overflow-y-auto pr-2">
                    {filteredItems.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
                            {paginatedItems.map((item) => (
                                <div
                                    key={item.id}
                                    className="bg-white rounded-2xl shadow-md hover:shadow-xl cursor-pointer transition-all duration-300 p-5"
                                    // onClick={() =>
                                    //     navigate(`/guest/item/${item.id}`, {
                                    //         state: { title: item.name },
                                    //     })
                                    // }
                                >
                                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                                        {item.name}
                                    </h3>

                                    <p className="text-sm text-gray-500 mb-2 capitalize">
                                        {item.category}
                                    </p>

                                    <p className="text-indigo-600 font-semibold">
                                        ₦{item.cashPrice.toLocaleString()}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center mt-12">
                            <div className="bg-gray-100 rounded-2xl p-8 flex flex-col items-center">
                                <svg
                                    className="w-16 h-16 text-gray-400 mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                >

                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9 14l2-2m0 0l2-2m-2 2l2 2m-2-2l-2 2m6 4H7a2 2 0 01-2-2V7a2 2 0 012-2h7"
                                    />
                                </svg>
                                <h3 className="text-lg font-semibold text-gray-700 mb-1">
                                    No items found
                                </h3>
                                <p className="text-gray-500 text-center">
                                    Sorry, we couldn't find any items that match your search. Try adjusting your search or browse other categories.
                                </p>
                            </div>
                        </div>
                    )}

                    {filteredItems.length > ITEMS_PER_PAGE && (
                        <div className="w-full flex justify-center mt-6">
                            <div className="flex items-center justify-center gap-3 px-4 py-3 bg-white rounded-lg shadow-sm max-w-fit">

                                {/* Prev */}
                                <button
                                    disabled={page === 1}
                                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                    className={`px-5 py-2 rounded-md font-medium transition-colors ${page === 1
                                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                            : "bg-indigo-500 text-white hover:bg-indigo-600"
                                        }`}
                                >
                                    Prev
                                </button>

                                <span className="text-sm text-gray-600 font-medium">
                                    Page {page} of {totalPages}
                                </span>

                                {/* Next */}
                                <button
                                    disabled={page === totalPages}
                                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                    className={`px-5 py-2 rounded-md font-medium transition-colors ${page === totalPages
                                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                            : "bg-indigo-500 text-white hover:bg-indigo-600"
                                        }`}
                                >
                                    Next
                                </button>

                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}